import { Observable } from 'rxjs/Observable';
import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { ICondition } from '../../interfaces/condition.interface';
import { IEffect } from '../../interfaces/effect.interface';
import { INumericValue } from '../../interfaces/numeric-value.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';
import { EVSEStatus } from './evse-status.enum';

export class EVSEServerPlugin extends SmartenitPlugin
  implements ICondition, IEffect, INumericValue {
  public static readonly CURRENT_DIVISOR = 100;
  public static readonly SUMMATION_DIVISOR = 100000;
  private static readonly BATTERY_LEVEL_MIN = 0;
  private static readonly BATTERY_LEVEL_MAX = 254;

  get stateTTL(): number {
    return 1 * 60;
  }

  public getReportableAttrs() {
    return ['ChargerStatus', 'PilotState', 'ChargerSessionActive', 'ChargerSessionSummation', 'ChargerSessionDuration',
    'ChargerDeliveredSummation', 'ChargerVRMS', 'ChargerIRMS', 'ChargerLineFrequency', 'ChargerInstantaneousDemand'];
  }

  /**
   * Conditions
   */

  public getConditionOptions() {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.EVSE.STATUS.UNPLUGGED',
            ''
          )
        ),
        name: 'NotPluggedIn',
        value: EVSEStatus.NotPluggedIn
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.EVSE.STATUS.NOT_CHARGING',
            ''
          )
        ),
        name: 'PluggedNotCharging',
        value: EVSEStatus.PluggedNotCharging
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.EVSE.STATUS.READY',
            ''
          )
        ),
        name: 'PluggedStarted',
        value: EVSEStatus.PluggedStarted
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.EVSE.STATUS.CHARGING',
            ''
          )
        ),
        name: 'Charging',
        value: EVSEStatus.Charging
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.EVSE.STATUS.FAULT',
            ''
          )
        ),
        name: 'InError',
        value: EVSEStatus.InError
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'DEVICES.EVSE.STATUS.CHARGED',
            ''
          )
        ),
        name: 'ChargeCompleted',
        value: EVSEStatus.ChargeCompleted
      }
    ];
  }

  public getConditionAttribute(): string | null {
    return 'ChargerStatus';
  }

  public setValue(value: number, attribute?: string | undefined): void {
  }

  public getValue(attribute?: string | undefined): number {
    return 0;
  }

  public getUnit(attribute?: string | undefined): string {
    return 'kW';
  }

  /**
   * Effects
   */

  public getEffectOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.START_CHARGING_SESSION',
            ''
          )
        ),
        name: 'start',
        value: {
          method: 'StartCharge'
        }
      }, {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.EFFECTS_NL.STOP_CHARGING_SESSION',
            ''
          )
        ),
        name: 'stop',
        value: {
          method: 'StopCharge'
        }
      }
    ];
  }

  public getEffectAttribute(): string {
    return 'null';
  }

  public getEffectMethod(context?: any): string | null {
    if (!context) {
      return null;
    }

    const currentMethod = context.method;
    if (['StartCharge', 'StopCharge'].indexOf(currentMethod) > -1) {
      return currentMethod;
    }

    return null;
  }

  /**
   * Session
   */

  public startSession() {
    this.executeMethod(
      this.componentId,
      this.processorName,
      'StartCharge',
      {},
      true,
      [
        'ChargerStatus',
        'ChargerSessionID',
        'ChargerSessionDuration',
        'ChargerDeliveredSummation',
        'ChargerSessionSummation',
        'ChargerInstantaneousDemand',
        'BatteryLevel',
      ]
    );
  }

  public stopSession() {
    this.executeMethod(
      this.componentId,
      this.processorName,
      'StopCharge',
      {},
      true,
      [
        'ChargerStatus',
        'ChargerSessionID',
        'ChargerSessionDuration',
        'ChargerDeliveredSummation',
        'ChargerInstantaneousDemand'
      ]
    );
  }

  public setAutoStartMode(active: boolean) {
    this.device.executeMethod(
      this.componentId,
      this.processorName,
      active ? 'EnableAutoStartMode' : 'DisableAutoStartMode',
      {},
      false,
      undefined,
      undefined,
      true
    );
    setTimeout(() => {
      this.getAutoStart();
    }, 1000);
  }

  /**
   * Estimations
   */

  public setBatteryCapacity(capacity: number) {
    this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'BatteryCapacity',
      { value: capacity * EVSEServerPlugin.SUMMATION_DIVISOR },
      false
    );
  }

  public setElectricRange(distance: number) {
    this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ElectricRange',
      { value: distance * EVSEServerPlugin.SUMMATION_DIVISOR },
      false
    );
  }

  public getBatteryLevel() {
    this.getAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'BatteryLevel',
      null,
      false,
      'cached=false'
    );
  }

  public setBatteryLevel(level: number) {
    const value = this.pluginUtilsService.getValueFromPercentage(
      level || 0,
      EVSEServerPlugin.BATTERY_LEVEL_MIN,
      EVSEServerPlugin.BATTERY_LEVEL_MAX
    );
    this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'BatteryLevel',
      { value },
      false
    );
  }

  /**
   * Attributes
   */

  public setMinimumCurrent(amps: number) {
    this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ChargerMinimumCurrent',
      { value: amps * EVSEServerPlugin.CURRENT_DIVISOR },
      false
    );
  }

  public setMaximumCurrent(amps: number) {
    this.setAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ChargerMaximumCurrent',
      { value: amps * EVSEServerPlugin.CURRENT_DIVISOR },
      false
    );
  }

  public getAutoStart() {
    this.getAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ChargerAutoStart',
      null,
      false,
      'cached=false'
    );
  }

  public getFault() {
    this.getAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ChargerFault',
      null,
      false,
      'cached=false'
    );
  }

  public getVoltage() {
    this.getAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ChargerVRMS',
      null,
      false,
      'cached=false'
    );
  }

  public getCurrent() {
    this.getAttributeWithSubscribeOption(
      this.componentId,
      this.processorName,
      'ChargerIRMS',
      null,
      false,
      'cached=false'
    );
  }

  /**
   * Status
   */

  public getStatusPayload(): any {
    return [
      'ChargerStatus',
      'ChargerLevel',
      'ChargerAutoStart',
      'ChargerMinimumCurrent',
      'ChargerMaximumCurrent',
      'ChargerSessionID',
      'ChargerSessionDuration',
      'ChargerDeliveredSummation',
      'ChargerSessionSummation',
      'ChargerInstantaneousDemand',
      'ChargerVRMS',
      'ChargerIRMS',
      'BatteryCapacity',
      'ElectricRange',
      'BatteryLevel',
    ];
  }

  public processMessage(message: IWebSocketDeviceMessage, timestamp?: number) {
    const response = message && message.data && message.data.response;
    const attribute = message && message.attributeOrMethod;

    if (attribute === 'ChargerStatus') {
      const chargerStatus = !isNaN(response.value)
        ? Number(response.value)
        : null;

      if (chargerStatus != null && chargerStatus !== this.state.chargerStatus) {
        this.getBatteryLevel();
      }

      this.state.chargerStatus = chargerStatus;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ChargerFault') {
      const chargerFault = !isNaN(response.value)
        ? Number(response.value)
        : null;
      this.state.chargerFault = chargerFault;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'ChargerLevel') {
      const chargerLevel = !isNaN(response.value)
        ? Number(response.value)
        : null;
      this.state.chargerLevel = chargerLevel;
      this._onUpdate.next({ state: this.state });
    } else if (attribute === 'StartChargeResponse') {
      this._onUpdate.next({ startChargeResponse: response.value });
    } else if (attribute === 'StopChargeResponse') {
      this._onUpdate.next({ stopChargeResponse: response.value });
    } else if (attribute === 'BatteryLevel') {
      this.state.batteryLevel = this.pluginUtilsService.getValueToPercentage(
        response.value || 0,
        EVSEServerPlugin.BATTERY_LEVEL_MIN,
        EVSEServerPlugin.BATTERY_LEVEL_MAX
      );
      this._onUpdate.next({ state: this.state });
    } else if (
      attribute === 'ChargerVRMS' ||
      attribute === 'ChargerIRMS' ||
      attribute === 'ChargerMaximumCurrent' ||
      attribute === 'ChargerMinimumCurrent'
    ) {
      const measuredValue = !isNaN(response.value)
        ? _.round(Number(response.value) / EVSEServerPlugin.CURRENT_DIVISOR, 2)
        : NaN;
      this.state[
        `${attribute[0].toLowerCase()}${attribute.substring(1)}`
      ] = !isNaN(measuredValue) ? measuredValue : null;
      this._onUpdate.next({ state: this.state });
    } else if (
      attribute === 'ChargerDeliveredSummation' ||
      attribute === 'ChargerSessionSummation' ||
      attribute === 'ChargerInstantaneousDemand' ||
      attribute === 'BatteryCapacity' ||
      attribute === 'ElectricRange'
    ) {
      const measuredValue = !isNaN(response.value)
        ? _.round(
          Number(response.value) / EVSEServerPlugin.SUMMATION_DIVISOR,
          2
        )
        : NaN;
      this.state[
        `${attribute[0].toLowerCase()}${attribute.substring(1)}`
      ] = !isNaN(measuredValue) ? measuredValue : null;
      this._onUpdate.next({ state: this.state });
    } else if (attribute) {
      this.state[`${attribute[0].toLowerCase()}${attribute.substring(1)}`] =
        response.value;
      this._onUpdate.next({ state: this.state });
    }

    return this.state;
  }

  public getRawValue(
    value: number,
    attribute?: string,
    method?: string
  ): number {
    if (attribute === 'BatteryLevel') {
      return this.pluginUtilsService.getValueFromPercentage(
        value,
        EVSEServerPlugin.BATTERY_LEVEL_MIN,
        EVSEServerPlugin.BATTERY_LEVEL_MAX
      );
    } else {
      return value;
    }
  }

  public getProcessedValue(value: any, attribute?: string, method?: string) {
    if (attribute === 'BatteryLevel') {
      return this.pluginUtilsService.getValueToPercentage(
        value,
        EVSEServerPlugin.BATTERY_LEVEL_MIN,
        EVSEServerPlugin.BATTERY_LEVEL_MAX
      );
    } else {
      return value;
    }
  }
}
